"use client";

import React, { useRef, useEffect, useState, useCallback } from "react";

interface ToggleOption {
  value: string;
  label: string;
  shortLabel?: string;
}

interface SlidingToggleGroupProps {
  options: ToggleOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  itemClassName?: string;
}

export function SlidingToggleGroup({
  options,
  value,
  onChange,
  className = "",
  itemClassName = "",
}: SlidingToggleGroupProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [sliderStyle, setSliderStyle] = useState<React.CSSProperties>({
    width: 0,
    left: 0,
  });

  const updateSlider = useCallback(() => {
    const activeIndex = options.findIndex((o) => o.value === value);
    const activeEl = itemRefs.current[activeIndex];
    const container = containerRef.current;
    if (!activeEl || !container) return;

    const containerRect = container.getBoundingClientRect();
    const activeRect = activeEl.getBoundingClientRect();

    setSliderStyle({
      width: activeRect.width,
      left: activeRect.left - containerRect.left,
    });
  }, [options, value]);

  useEffect(() => {
    updateSlider();
  }, [updateSlider]);

  useEffect(() => {
    window.addEventListener("resize", updateSlider);
    return () => window.removeEventListener("resize", updateSlider);
  }, [updateSlider]);

  return (
    <div
      className={`relative isolate flex items-center rounded-lg outline outline-1 outline-gray-200 dark:outline-white/20 !bg-gray-50 dark:!bg-gray-800 overflow-hidden ${className}`}
      ref={containerRef}
    >
      <div
        className="absolute inset-0 bg-blue-500 dark:bg-primary rounded-lg transition-all duration-300 ease-in-out"
        style={{ width: sliderStyle.width, left: sliderStyle.left }}
      />
      {options.map((option, index) => (
        <button
          className={`relative z-10 flex-1 px-4 py-2 text-sm rounded-lg transition-colors duration-300 text-center whitespace-nowrap ${
            value === option.value
              ? "text-white font-semibold"
              : "text-black dark:text-white font-medium"
          } ${itemClassName}`}
          key={option.value}
          onClick={() => onChange(option.value)}
          ref={(el) => {
            itemRefs.current[index] = el;
          }}
          type="button"
        >
          {option.shortLabel ? (
            <>
              <span className="sm:hidden">{option.shortLabel}</span>
              <span className="hidden sm:inline">{option.label}</span>
            </>
          ) : (
            option.label
          )}
        </button>
      ))}
    </div>
  );
}
